import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import DataTable from '@/util/DataTable';
import ViewRef from '@/util/ViewRef';
import {
  CHANGE_TAB,
  DASHBOARD_CONTENT_LOADED,
  DASHBOARD_UPDATED,
  MODAL_INTERACTION,
  SELECT_SAVED_QUERY_FOR_WIDGET,
} from '@/actions/constants';
import { buildDataTable } from '@/reducers/rows';
import DashboardChartWidget from '@/util/DashboardChartWidget';
import { UUID } from '@/types';
import ColumnName from '@/util/ColumnName';
import { TChartType } from '@/features/chart-configuration/apiSlice';
import { PIE } from '@/components/charting/chartType.constants';

type State = {
  isWidgetConfigurationSidebarOpen: boolean;
  isSharePublishEmbedSidebarOpen: boolean;
  selectedChartWidgetIndex: Maybe<number>;
  chartConfiguration: Maybe<unknown[]>;
  dataTable: Maybe<DataTable>;
  chartType: TChartType;
  metrics: Nullable<ColumnName[]>;
  dimensions: Nullable<ColumnName[]>;
  widgetTitle: Maybe<string>;
  isLoading: boolean;
  widgetView: Maybe<ViewRef>; // TODO: replace with SavedQuery after refactoring
  widget: Maybe<DashboardChartWidget>;
  filtersUpdated: boolean;
  filters: {
    dashboardFiltersIds: UUID[];
    filtersIdsToRemove: UUID[];
    matchingColumns: ColumnName[];
  };
};

const initialState: State = {
  isWidgetConfigurationSidebarOpen: false,
  isSharePublishEmbedSidebarOpen: false,
  isLoading: false,
  selectedChartWidgetIndex: null,
  chartConfiguration: null,
  dataTable: null,
  chartType: 'table',
  metrics: null,
  dimensions: null,
  widgetView: null,
  widgetTitle: null,
  widget: null,
  filtersUpdated: false,
  filters: {
    dashboardFiltersIds: [],
    filtersIdsToRemove: [],
    matchingColumns: [],
  },
};

type Defined<P extends keyof State> = NonNullable<State[P]>;

const resetState = (_state: Draft<State>) => initialState;

export const dashboardSlice = createSlice({
  name: 'dashboardSlice',
  initialState,
  reducers: {
    toggleSharePublishEmbedSidebar: (state) => {
      state.isSharePublishEmbedSidebarOpen =
        !state.isSharePublishEmbedSidebarOpen;
    },
    toggleWidgetConfigurationSidebar: (state) => {
      state.isWidgetConfigurationSidebarOpen =
        !state.isWidgetConfigurationSidebarOpen;
      if (!state.isWidgetConfigurationSidebarOpen) {
        state.chartType = initialState.chartType;
      }

      // Do not allow 2 sidebars opened at the same time
      if (state.isSharePublishEmbedSidebarOpen) {
        state.isSharePublishEmbedSidebarOpen = false;
      }
    },
    selectWidget: (
      state,
      action: PayloadAction<
        Partial<{
          view: State['widgetView'];
          chartConfiguration: State['chartConfiguration'];
          selectedChartWidgetIndex: Defined<'selectedChartWidgetIndex'>;
          dataTable: State['dataTable'];
          displayType: Defined<'chartType'>;
          widgetTitle: State['widgetTitle'];
          widget: Defined<'widget'>;
        }>
      >,
    ) => {
      state.selectedChartWidgetIndex = action.payload.selectedChartWidgetIndex;
      state.chartConfiguration = action.payload.chartConfiguration;
      state.dataTable = action.payload.dataTable;
      state.chartType = action.payload.displayType ?? initialState.chartType;
      state.widgetView = action.payload.view;
      state.widgetTitle = action.payload.widgetTitle;
      state.widget = action.payload.widget;
      state.isWidgetConfigurationSidebarOpen = true;
      // backward compatibility for the existing widgets
      if (
        state.chartType === PIE &&
        !state.widget?.metricColumns &&
        !state.widget?.dimensionColumns &&
        state.dataTable?._headings?.length === 3
      ) {
        state.chartType = 'stacked';
      }
    },
    resetWidget: resetState,
    updateDisplayType: (state, action: PayloadAction<State['chartType']>) => {
      state.chartType = action.payload;
    },
    updateChartConfiguration: (
      state,
      action: PayloadAction<State['chartConfiguration']>,
    ) => {
      state.chartConfiguration = action.payload;
    },
    updateWidgetTitle: (state, action: PayloadAction<State['widgetTitle']>) => {
      state.widgetTitle = action.payload;
    },
    updateFilter: (
      state,
      action: PayloadAction<{ filterId: UUID; column: ColumnName }>,
    ) => {
      const { filterId, column } = action.payload;

      if (state.filters.dashboardFiltersIds.includes(filterId)) {
        const index = state.filters.dashboardFiltersIds.indexOf(filterId);
        state.filters.matchingColumns[index] = column;
      } else {
        state.filters.dashboardFiltersIds.push(filterId);
        state.filters.matchingColumns.push(column);
      }

      if (state.filters.filtersIdsToRemove.includes(filterId)) {
        const index = state.filters.filtersIdsToRemove.indexOf(filterId);
        state.filters.filtersIdsToRemove.splice(index, 1);
      }
      state.filtersUpdated = true;
    },
    removeFilter: (state, action: PayloadAction<UUID>) => {
      const index = state.filters.dashboardFiltersIds.indexOf(action.payload);

      state.filters.filtersIdsToRemove = [
        ...new Set([...state.filters.filtersIdsToRemove, action.payload]),
      ];

      state.filters.dashboardFiltersIds.splice(index, 1);
      state.filters.matchingColumns.splice(index, 1);
      state.filtersUpdated = true;
    },
    resetWidgetSavedQuery: (state) => ({
      ...initialState,
      selectedChartWidgetIndex: state.selectedChartWidgetIndex,
      isWidgetConfigurationSidebarOpen: state.isWidgetConfigurationSidebarOpen,
      widget: state.widget,
    }),
    setMetrics: (state, action: PayloadAction<State['metrics']>) => {
      state.metrics = action.payload;
    },
    setDimensions: (state, action: PayloadAction<State['dimensions']>) => {
      state.dimensions = action.payload;
    },
  },
  extraReducers: {
    [SELECT_SAVED_QUERY_FOR_WIDGET]: (state) => {
      state.isLoading = true;
    },
    [DASHBOARD_CONTENT_LOADED]: (state, action) => {
      const response = action.payload.json;
      const index = response._views.findIndex(
        (v: ViewRef) => v._series === state?.widgetView?._series,
      );
      if (
        state.widgetView &&
        index !== -1 &&
        response._widgetContents[index]._status === 'success'
      ) {
        const { _chartConfig, _dataTable } = response._widgetContents[index];

        state.dataTable = buildDataTable(_dataTable);
        state.chartConfiguration = _chartConfig;
        state.isLoading = false;
        if (
          state.chartType === PIE &&
          !state.widget?.metricColumns &&
          !state.widget?.dimensionColumns &&
          state.dataTable?._headings?.length === 3
        ) {
          state.chartType = 'stacked';
        }
      }
    },
    [DASHBOARD_UPDATED]: resetState,
    [CHANGE_TAB]: resetState,
    [MODAL_INTERACTION]: (state, action) => {
      if (action.payload?.props?.section === 'deleteWidget') {
        return initialState;
      }
    },
  },
});

export const selectIsWidgetConfigurationSidebarOpen = (state: RootState) =>
  state.dashboardSlice.isWidgetConfigurationSidebarOpen;

export const selectIsSharePublishEmbedSidebarOpen = (state: RootState) =>
  state.dashboardSlice.isSharePublishEmbedSidebarOpen;

export const selectChartConfiguration = (state: RootState) =>
  state.dashboardSlice.chartConfiguration;

export const selectChartWidgetIndex = (state: RootState) =>
  state.dashboardSlice.selectedChartWidgetIndex;

export const selectedChartWidget = (state: RootState) =>
  state.dashboardSlice.widget;

export const selectDataTable = (state: RootState) =>
  state.dashboardSlice.dataTable;

export const selectChartType = (state: RootState) =>
  state.dashboardSlice.chartType;

export const selectMetrics = (state: RootState) => state.dashboardSlice.metrics;

export const selectDimensions = (state: RootState) =>
  state.dashboardSlice.dimensions;

export const selectWidgetView = (state: RootState) =>
  state.dashboardSlice.widgetView;

export const selectWidgetTitle = (state: RootState) =>
  state.dashboardSlice.widgetTitle;

export const selectIsLoading = (state: RootState) =>
  state.dashboardSlice.isLoading;

export const selectFilters = (state: RootState) => state.dashboardSlice.filters;

export const selectIsFilterUpdated = (state: RootState) =>
  state.dashboardSlice.filtersUpdated;

export const {
  toggleSharePublishEmbedSidebar,
  toggleWidgetConfigurationSidebar,
  updateChartConfiguration,
  selectWidget,
  updateWidgetTitle,
  resetWidget,
  updateDisplayType,
  updateFilter,
  removeFilter,
  resetWidgetSavedQuery,
  setMetrics,
  setDimensions,
} = dashboardSlice.actions;
